import { ReactElement, useEffect } from 'react';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { UserService } from '@src/api/common/UserService';
import { JwtService } from '@src/api/common/JwtService';
import { useAuthSessionStore } from '@src/store/common/AuthSessionStore';

interface AuthGuardProps {
  children: ReactElement;
  requireAuth?: boolean;
}

export const AuthenticationGuard = ({
  children,
  requireAuth = true,
}: AuthGuardProps) => {
  const { navigate, getLocation } = useRouter();
  const location = getLocation();

  const clearAuthState = () => {
    localStorage.removeItem('isLoggedIn');
    UserService.ClearAuthenticationCookies();
    useAuthSessionStore.getState().syncFromCookies({ incrementEpoch: true });
  };

  const isAuthenticated = (): boolean => {
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    const token = UserService.GetToken();
    const user = UserService.GetUser();

    // If localStorage says logged in but no token/user, clear invalid state
    if (isLoggedIn && (!token || !user)) {
      clearAuthState();
      return false;
    }

    // If we have a token, validate its expiry
    if (token && JwtService.IsTokenExpired(token)) {
      clearAuthState();
      return false;
    }

    return isLoggedIn && !!token && !!user;
  };

  useEffect(() => {
    // Check authentication state BEFORE any clearing happens
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    //const token = UserService.GetToken();
    //const user = UserService.GetUser();
    //const hasAnySSOTokens = !!token || !!user;

    const authenticated = isAuthenticated();

    // Define public routes that should redirect authenticated users to inventory
    const publicRoutesForRedirect = ['/', '/login', '/sso'];

    // Check if current path is one of the public routes that should redirect
    const shouldRedirectToIndex = publicRoutesForRedirect.includes(
      location?.pathname || ''
    );

    if (requireAuth && !authenticated) {
      // For protected routes, show not-authenticated page instead of redirecting to login
      navigate('/not-authenticated');
    } else if (!requireAuth && authenticated && shouldRedirectToIndex) {
      // Only redirect to inventory for specific public routes, not for 404 pages or not-authenticated page
      navigate('/responses');
    }
  }, [requireAuth, navigate, location?.pathname]);

  return children;
};
